import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../../redux/initialStoreState";
import React from "react";
import {
  clearActivityState,
  fetchBusinessTasksListAsync,
  fetchDataTasksListFromArchiveAsync,
} from "../../../../../../redux/activity/activityActions";
import { useDispatchWrapper } from "../../../../../../hooks";
import {
  IBusinessTask,
  INITIAL_BUSINESS_TASK,
} from "../../../../../../redux/activity/activity.types";
import { useTableV2State } from "../../../../../../components/TableV2/hooks/useTableV2State";
import {
  DataTableV2RowRenderType,
  IDataTableV2Props,
} from "../../../../../../components/TableV2/interfaces/IDataTableV2Props";
import { LoadState } from "../../../../../../constants/enums";
import { StandardTableActions } from "../../../../../../components/StandardTableActions/StandardTableActions";
import { Button, Link, Stack } from "@mui/material";
import { AddOutlined, ArchiveOutlined } from "@mui/icons-material";
import { DataTableV2 } from "../../../../../../components/TableV2/DataTableV2";
import { ManageBusinessTask } from "../../../../../../components/Activity/BusinessTasks/ManageBusinessTask";
import { StatusRenderer } from "../../../../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { PageContainer } from "../../../../../../components/container/PageContainer";
import moment from "moment";
import { StandadCard } from "../../../../../../components/Cards";
import { IActivityModuleInformation } from "../../../../../../components/Activity/Activity.types";

export interface IPolicyNotesListProps {
  customerId: number;
  customerPolicyId: number;
  moduleInformation: IActivityModuleInformation;
  /**
   * When true, the activities list is fetched filtering by
   * `customer_policy_id` instead of `module_reference_code_or_id`, and the
   * default `module_name` advanced filter query is not applied.
   */
  useCustomerPolicyIdFilter?: boolean;
}

export const PolicyNotesList: React.FC<IPolicyNotesListProps> = (props) => {
  const { customerId, customerPolicyId, moduleInformation, useCustomerPolicyIdFilter } =
    props;

  const { list, loading, fromArchiveLoad, error } = useSelector(
    (storeState: IStoreState) => storeState.acitivty.tasks
  );

  const [open, setOpen] = React.useState<IBusinessTask | null>(null);
  const [status, setStatus] = React.useState<"ACTIVE" | "INACTIVE" | "-1">(
    "-1"
  );
  const dispatch = useDispatchWrapper();

  const {
    state: { dateState, searchState, tabs, columnsConfig, pagination, dense },
    setDateState,
    setSelectedTab,
    setSearchState,
    setColumnVisibility,
    setTableTabs,
    onPageChange,
    onRowsPerPageChange,

    onDenseToggle,
  } = useTableV2State({
    filtersInitialState: {
      defaultDateRange: "allTimes",
      selectedTab: "-1",
    },
    pagination: {
      pageNumber: 1,
      rowsInPerPage: 25,
      apiFetchRowCount: 26,
      extraFetchFactor: 1,
    },
  });

  const fetchList = () => {
    if (customerPolicyId) {
      dispatch(
        fetchBusinessTasksListAsync(
          pagination.pageNumber,
          pagination.rowsInPerPage,
          pagination.apiFetchRowCount,
          tabs.selectedTab,
          status,
          dateState.dates,
          searchState,
          useCustomerPolicyIdFilter ? undefined : moduleInformation.moduleId,
          undefined,
          undefined,
          undefined,
          undefined,
          useCustomerPolicyIdFilter ? undefined : moduleInformation.moduleName,
          useCustomerPolicyIdFilter ? String(customerPolicyId) : undefined
        )
      );
    }
  };

  const loadDataFromArchive = () => {
    dispatch(
      fetchDataTasksListFromArchiveAsync(
        pagination.pageNumber,
        pagination.rowsInPerPage,
        pagination.apiFetchRowCount,
        status,
        dateState.dates,
        {},
        searchState,
        moduleInformation.moduleId // customerId,
      )
    );
  };

  React.useEffect(() => {
    if(fromArchiveLoad){
      loadDataFromArchive();
    }
    else {
    fetchList();
    }

    return () => {
      dispatch(clearActivityState());
    };
  }, [
    customerPolicyId,
    status,
    tabs.selectedTab,
    dateState.dates,
    searchState,
    pagination,
  ]);

  const automobileInterestedTableProps: IDataTableV2Props = {
    isPagination: true,
    rowsPerPageOptions: pagination.rowsInPerPage,
    extraFetchFactor: pagination.extraFetchFactor,
    isDataLoading: loading === LoadState.InProgress,
    disableRowPerPageDropdown: true,
    tableCommandBarProps: {
      preDefinedPlugins: {
        dateFilter: {
          state: dateState,
          onChange: setDateState,
        },
        search: {
          state: searchState,
          onChange: setSearchState,
          loadInitialFilterOncePopoverOpened: [
            {
              column: ["task_name"],
              logicalOperator: "OR",
              operator: "CONTAINS",
              value: "",
            },
          ],
        },
        columnVisibility: {
          columnVisibility: columnsConfig.columnVisibility,
          onChange: setColumnVisibility,
        },
        refresh: {
          onClick: fetchList,
        },
      },
      leftItems: {},
      rightItems: {
        customPlugins: [
          {
            key: "action",
            onRender: () => (
              <Button
                variant="contained"
                startIcon={<AddOutlined />}
                onClick={() => {
                  setOpen({
                    ...INITIAL_BUSINESS_TASK,
                  });
                }}
              >
                Create
              </Button>
            ),
          },
        ],
      },
    },
    masterColumns: [
      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        isFirstColumnSticky: true,
        headingAlign: "center",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IBusinessTask) => {
          return (
            <StandardTableActions
              commentBoxTypeId={row.task_module_wise_code as any}
              onEditClick={() => {
                setOpen(row);
              }}
              onViewClick={() => {
                setOpen(row);
              }}
            />
          );
        },
      },
      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IBusinessTask) => {
          return (
            <StatusRenderer
              status={row.status === "ACTIVE" ? "Open" : "Closed"}
            />
          );
        },
      },

      {
        key: "code",
        headerName: "Code",
        fieldName: "customer_code",
        renderType: DataTableV2RowRenderType.CHIP_WARNING,
        // exportCellWidth: 30,
      },
      {
        key: "Customer",
        headerName: "Customer",
        fieldName: "customer_name",
        renderType: DataTableV2RowRenderType.TEXT,
        // exportCellWidth: 30,
      },
      {
        key: "Policy Number",
        headerName: "Policy Number",
        fieldName: "policy_number",
        renderType: DataTableV2RowRenderType.TEXT_DARK,
        // exportCellWidth: 30,
      },
      {
        key: "name",
        headerName: "Task",
        fieldName: "task_name",
        // exportCellWidth: 25,
        renderType: DataTableV2RowRenderType.TEXT,
        onRowCellRender: (value, row: IBusinessTask) => {
          return (
            <Link
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setOpen(row);
              }}
            >
              {row.task_name}
            </Link>
          );
        },
      },
      {
        key: "task_type",
        headerName: "Type",
        fieldName: "task_type",
        renderType: DataTableV2RowRenderType.TEXT,
        // exportCellWidth: 30,
      },
      {
        key: "branch_code",
        headerName: "Branch",
        fieldName: "branch_code",
        renderType: DataTableV2RowRenderType.TEXT,
        // exportCellWidth: 30,
      },
      {
        key: "producer_one_code",
        headerName: "Producer",
        fieldName: "producer_one_code",
        renderType: DataTableV2RowRenderType.TEXT,
        // exportCellWidth: 30,
      },
      {
        key: "created_by_name",
        headerName: "Created By",
        fieldName: "created_by_name",
        renderType: DataTableV2RowRenderType.TEXT,
        // exportCellWidth: 30,
      },
      {
        key: "assigned_to_name",
        headerName: "Assigned To",
        fieldName: "assigned_to_name",
        renderType: DataTableV2RowRenderType.TEXT,
        // exportCellWidth: 30,
      },
      {
        key: "Modified By",
        headerName: "Modified By",
        fieldName: "modified_by_name",
        renderType: DataTableV2RowRenderType.TEXT,
        // exportCellWidth: 30,
      },
      {
        key: "due_date",
        headerName: "Due Date",
        fieldName: "due_date",
        renderType: DataTableV2RowRenderType.DATE,
        // exportCellWidth: 30,
      },

      {
        key: "due_time",
        headerName: "Due Time",
        fieldName: "due_time",
        renderType: DataTableV2RowRenderType.TEXT,
        onRowCellValueRender: (value) => {
          return value ? moment(`1970-01-01T${value}`).format("hh:mm A") : "-";
        },
      },

      {
        key: "create_ts",
        headerName: "Date Created",
        fieldName: "create_ts",
        renderType: DataTableV2RowRenderType.DATE_TIME,
        // exportCellWidth: 30,
      },
      {
        key: "insert_ts",
        headerName: "Date Modified",
        fieldName: "insert_ts",
        renderType: DataTableV2RowRenderType.DATE_TIME,
        // exportCellWidth: 30,
      },
    ],
    rows: list,
    dense: dense,
    onPageChange: onPageChange,
    onRowsPerPageChange: onRowsPerPageChange,
    onDenseToggle: onDenseToggle,
  };

  return (
    <PageContainer title="Activites" description="this is inner page">
      {/* <PageTitleBar
        sx={{ mt: 2 }}
        heading="Policy Notes"
        rightHeading={
          
        }
      /> */}
      <DataTableV2 {...automobileInterestedTableProps} />

      <StandadCard>
        <Stack
          direction={"row"}
          justifyContent={"center"}
          alignItems={"center"}
          sx={{ mt: 5, mb: 5 }}
        >
          <Stack
            direction={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            spacing={2}
          >
            <Button
              variant="contained"
              startIcon={<ArchiveOutlined />}
              onClick={loadDataFromArchive}
            >
              Load More Data From Archive
            </Button>
          </Stack>
        </Stack>
      </StandadCard>

      {open && (
        <ManageBusinessTask
          open={true}
          task={open}
          moduleInformation={moduleInformation}
          onClose={() => setOpen(null)}
          showCustomerAndPolicy
          customer_id={customerId}
          customer_policy_id={customerPolicyId}
          onComplete={() => {
            setOpen(null);
            fetchList();
          }}
        />
      )}
    </PageContainer>
  );
};
